/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, March 24th 2023, 3:45:55 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { useSearchParams } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import WindowIcon from "@mui/icons-material/Window";
import { LayoutToggler } from "service-builder/components/layouts";
import { ContextTagSelectorButtonWrapper } from "common/components/context-tags";
import { IconText, OSActionButton } from "ui-widgets";
import { DesignLayout } from "lib/schemas";
import { useServiceBuilderContext } from "service-builder/providers";
import { InstanceDetailLayout } from "./InstanceDetailLayout";
import { InstanceListLayout } from "./InstanceListLayout";

const layoutTypeData: React.ComponentProps<typeof LayoutToggler>["layoutTypeData"] = [
  {
    layoutType: "detail_display",
    layoutTypeTitle: "Instance Detail Layout",
    layoutTypeColor: "primary",
  },
  {
    layoutType: "list_display",
    layoutTypeTitle: "Instance List Layout",
    layoutTypeColor: "info",
  },
];

type InstanceLayoutTypes = typeof layoutTypeData[number]["layoutType"];

export const InstanceLayoutsView = () => {
  const [searchParams, setSearchParams] = useSearchParams({ layoutType: "detail_display" });
  const layoutType = searchParams.get("layoutType") as InstanceLayoutTypes | null;

  const pageTitle = `${
    layoutType === "list_display" ? "Instance List Layout Configuration" : "Instance Detail Layout Design"
  }`;

  const {
    processDefinitionContext: { currentProcessDefinition },
    patchCurrentProcessDefinition,
  } = useServiceBuilderContext();

  const onSaveDetailLayout = (designLayout: DesignLayout) => {
    if (currentProcessDefinition.detail_view_layout === designLayout.id) return;

    patchCurrentProcessDefinition({ detail_view_layout: designLayout.id });
  };

  const onSaveListLayout = (designLayout: DesignLayout) => {
    if (currentProcessDefinition.list_view_layout === designLayout.id) return;

    patchCurrentProcessDefinition({ list_view_layout: designLayout.id });
  };

  const [editorContentToAppend, setEditorContentToAppend] = React.useState<string>(null);

  const onCopyContextTag = ({ tag, tagName }) => setEditorContentToAppend(`${tag} - ${tagName}`);

  let showTopbar: boolean = false;

  switch (layoutType) {
    case "detail_display":
      showTopbar = Boolean(currentProcessDefinition?.detail_view_layout);
      break;
    case "list_display":
      showTopbar = Boolean(currentProcessDefinition?.list_view_layout);
      break;
  }

  const showContextTagSelector = layoutType === "detail_display";

  const component =
    layoutType === "list_display" ? (
      <InstanceListLayout onSaveListLayout={onSaveListLayout} processDefinition={currentProcessDefinition} />
    ) : (
      <InstanceDetailLayout
        contextTagsToAppend={editorContentToAppend}
        onSaveDetailLayout={onSaveDetailLayout}
        processDefinition={currentProcessDefinition}
      />
    );

  return (
    <Grid container spacing={2} sx={{ padding: 1 }}>
      <Grid item xs={showTopbar ? 7 : 11}>
        <IconText
          icon={<WindowIcon color="primary" />}
          textComponent={<Typography variant="h5">{pageTitle}</Typography>}
        />
      </Grid>
      {showTopbar ? (
        <Grid item xs={4}>
          <div style={{ marginLeft: "auto" }}>
            <Stack direction="row" spacing={1}>
              {showContextTagSelector ? (
                <ContextTagSelectorButtonWrapper
                  onCopyContextTag={onCopyContextTag}
                  copiedMessage="Added to layout"
                  contextTagsAPIFilters={{ process_definition: currentProcessDefinition.id }}
                >
                  {({ openContextTagsFn }) => (
                    <OSActionButton onClick={openContextTagsFn} variant="contained">
                      Use Context Tags
                    </OSActionButton>
                  )}
                </ContextTagSelectorButtonWrapper>
              ) : null}
            </Stack>
          </div>
        </Grid>
      ) : null}
      <Grid item xs={1}>
        <LayoutToggler
          selectedLayoutType={layoutType}
          toggleLayoutType={(layoutType) => setSearchParams({ layoutType })}
          layoutTypeData={layoutTypeData}
        />
      </Grid>
      <Grid item xs={12}>
        {component}
      </Grid>
    </Grid>
  );
};
